import { Injectable } from '@angular/core';

import { finalize, map, Observable } from 'rxjs';

import { IdentityBodyAssessReading } from '@reg/shared/model';
import { HttpServiceHelper, IdentityBodyAssessService } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';

/**
 * Service to get and save bodyAssessReading data.
 */
@Injectable()
export class IdentityBodyAssessReadingService {
    private identityApiBaseUrl: string;

    /**
     * Constructor
     */
    constructor(
                private http: HttpServiceHelper,
                private identityBodyAssessService: IdentityBodyAssessService,
    ) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get all readings for a given body assessment
     */
    public getIndex(bodyAssessmentId: string, includes: string[] = null): Observable<IdentityBodyAssessReading[]> {
        const apiRequestUrl: string = this.getApiUrl(bodyAssessmentId, null, includes, null);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const bodyAssessReadings: IdentityBodyAssessReading[] = [];
                const included: Object[] = response.included;

                const bodyData: any = response.data || [];
                for (const bodyAssessReading of bodyData) {
                    bodyAssessReadings.push((new IdentityBodyAssessReading()).deserialize(bodyAssessReading, included));
                }

                return bodyAssessReadings;
            }));
    }

    /**
     * Get a single bodyAssessmentReading
     */
    public get(
        identityBodyAssessmentId: string,
        identityBodyAssessReadingId: string,
        includes: string[] = null,
    ): Observable<IdentityBodyAssessReading> {
        const apiRequestUrl: string = this.getApiUrl(identityBodyAssessmentId, identityBodyAssessReadingId, includes);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new IdentityBodyAssessReading().deserialize(response))));
    }

    /**
     * Add or update a bodyAssessmentReading
     */
    public save(
        bodyAssessReading: IdentityBodyAssessReading,
        includes: string[] = null,
    ): Observable<IdentityBodyAssessReading> {
        let response: Observable<IJsonApiResponse>;
        const bodyAssessReadingId: string = bodyAssessReading.isNew() ? null : bodyAssessReading.id;
        const apiRequestUrl: string = this.getApiUrl(bodyAssessReading.bodyAssessmentId, bodyAssessReadingId, includes);

        if (bodyAssessReadingId) {
            // Update existing BodyAssessReading
            response = this.http.patch(apiRequestUrl, bodyAssessReading.serialize());
        } else {
            // Add a new BodyAssessReading
            response = this.http.post(apiRequestUrl, bodyAssessReading.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityBodyAssessReading()).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        bodyAssessmentId: string,
        bodyAssessReadingId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.identityApiBaseUrl}/body-assessments/${bodyAssessmentId}/readings/`;

        if (bodyAssessReadingId) {
            url += bodyAssessReadingId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private clearCache(): void {
        this.identityBodyAssessService.clearCache();
    }
}

